'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MultiSelectDropdown, MultiSelectOption } from '@/components/ui/multi-select-dropdown';
import { metadataApi } from '@/lib/api/endpoints';
import clsx from 'clsx';

// Types corresponding to the filter configuration
export type FilterConfig = {
  key: string;
  label: string;
  type: 'tags' | 'select' | 'multiselect';
  options?: MultiSelectOption[];
  // For 'tags', we fetches from metadata 'item_tags' automatically or use options if provided
};
export interface FilterViewProps {
  searchEnabled?: boolean;
  tagsEnabled?: boolean;
  canFilter?: boolean;
  searchValue?: string;
  onSearch?: (term: string) => void;
  filters: FilterConfig[];
  activeFilters: Record<string, any>; // key -> value
  onFilterChange: (newFilters: Record<string, any>) => void;
  className?: string;
}
interface TagCategory {
  id: number;
  name: string;
  items: {
    id: number;
    name: string;
  }[];
}
export const FilterView = React.memo(function FilterView(t0) {
  const $ = _c(59);
  const {
    searchEnabled: t1,
    tagsEnabled: t2,
    searchValue,
    onSearch,
    filters,
    activeFilters,
    onFilterChange,
    className,
    canFilter: t3
  } = t0;
  const searchEnabled = t1 === undefined ? true : t1;
  const tagsEnabled = t2 === undefined ? true : t2;
  const canFilter = t3 === undefined ? true : t3;
  const [searchTerm, setSearchTerm] = useState(searchValue || "");
  const [isOpen, setIsOpen] = useState(false);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [tagOptions, setTagOptions] = useState(t4);
  const searchInputRef = useRef(null);
  const isUserTypingRef = useRef(false);
  const [tempFilters, setTempFilters] = useState(activeFilters);
  let t5;
  if ($[1] !== searchTerm || $[2] !== searchValue) {
    t5 = () => {
      if (searchValue !== undefined && searchValue !== searchTerm && !isUserTypingRef.current) {
        setSearchTerm(searchValue);
      }
    };
    $[1] = searchTerm;
    $[2] = searchValue;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== searchValue) {
    t6 = [searchValue];
    $[4] = searchValue;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const [tagsResponse] = useState(t7);
  let t8;
  let t9;
  if ($[7] !== tagsResponse) {
    t8 = () => {
      if (tagsResponse) {
        setTagOptions(tagsResponse);
      }
    };
    t9 = [tagsResponse];
    $[7] = tagsResponse;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  useEffect(t8, t9);
  let t10;
  if ($[10] !== activeFilters) {
    t10 = () => {
      setTempFilters(activeFilters);
    };
    $[10] = activeFilters;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== activeFilters || $[13] !== isOpen) {
    t11 = [isOpen, activeFilters];
    $[12] = activeFilters;
    $[13] = isOpen;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  useEffect(t10, t11);
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e => {
      const term = e.target.value;
      isUserTypingRef.current = true;
      setSearchTerm(term);
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const handleSearch = t12;
  const onSearchRef = useRef(onSearch);
  let t13;
  let t14;
  if ($[16] !== onSearch) {
    t13 = () => {
      onSearchRef.current = onSearch;
    };
    t14 = [onSearch];
    $[16] = onSearch;
    $[17] = t13;
    $[18] = t14;
  } else {
    t13 = $[17];
    t14 = $[18];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[19] !== searchTerm) {
    t15 = () => {
      if (!isUserTypingRef.current) {
        return;
      }
      const timer = setTimeout(() => {
        onSearchRef.current?.(searchTerm);
        isUserTypingRef.current = false;
      }, 200);
      return () => clearTimeout(timer);
    };
    t16 = [searchTerm];
    $[19] = searchTerm;
    $[20] = t15;
    $[21] = t16;
  } else {
    t15 = $[20];
    t16 = $[21];
  }
  useEffect(t15, t16);
  let t17;
  if ($[22] !== onFilterChange || $[23] !== tempFilters) {
    t17 = () => {
      onFilterChange(tempFilters);
      setIsOpen(false);
    };
    $[22] = onFilterChange;
    $[23] = tempFilters;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const handleApply = t17;
  let t18;
  if ($[25] !== activeFilters || $[26] !== onFilterChange) {
    t18 = (key, value) => {
      const current = activeFilters[key];
      if (Array.isArray(current)) {
        const newValues = current.filter(v => v !== value);
        onFilterChange({
          ...activeFilters,
          [key]: newValues
        });
      } else {
        const newFilters = {
          ...activeFilters
        };
        delete newFilters[key];
        onFilterChange(newFilters);
      }
    };
    $[25] = activeFilters;
    $[26] = onFilterChange;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const removeFilter = t18;
  let t19;
  if ($[28] !== activeFilters || $[29] !== filters || $[30] !== removeFilter) {
    t19 = () => {
      const chips = [];
      Object.entries(activeFilters).forEach(t20 => {
        const [key_0, value_0] = t20;
        const config = filters.find(f => f.key === key_0);
        if (!config) {
          return;
        }
        if (Array.isArray(value_0)) {
          value_0.forEach(val => {
            chips.push(<div key={`${key_0}-${val}`} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-sm"><span>{val}</span><button onClick={() => removeFilter(key_0, val)} className="text-gray-400 hover:text-gray-600"><X className="w-3 h-3" /></button></div>);
          });
        }
      });
      return chips;
    };
    $[28] = activeFilters;
    $[29] = filters;
    $[30] = removeFilter;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const renderActiveFilters = t19;
  let t20;
  if ($[32] !== className) {
    t20 = clsx("w-full space-y-4", className);
    $[32] = className;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== searchEnabled || $[35] !== searchTerm) {
    t21 = searchEnabled && <div className="relative flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" /><Input ref={searchInputRef} placeholder="Enter a brand name, product, or category" value={searchTerm} onChange={handleSearch} onFocus={() => {
        isUserTypingRef.current = true;
      }} onBlur={() => {
        setTimeout(() => {
          isUserTypingRef.current = false;
        }, 100);
      }} className="pl-9 h-10 border-orange-200 focus-visible:ring-orange-500" /></div>;
    $[34] = searchEnabled;
    $[35] = searchTerm;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== canFilter || $[38] !== filters || $[39] !== handleApply || $[40] !== isOpen || $[41] !== tagOptions || $[42] !== tagsEnabled || $[43] !== tempFilters) {
    t22 = canFilter && tagsEnabled && <Popover open={isOpen} onOpenChange={setIsOpen}><PopoverTrigger asChild={true}><Button variant="outline" className="h-10 px-4 border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700 gap-2 shrink-0"><SlidersHorizontal className="w-4 h-4" />Filter</Button></PopoverTrigger><PopoverContent align="end" className="w-[300px] p-4"><div className="space-y-4">{filters.map(filter => {
            if (filter.type === "tags" || filter.type === "multiselect") {
              const currentOptions = filter.type === "tags" ? tagOptions : filter.options || [];
              const selected = tempFilters[filter.key] as string[] || [];
              return <div key={filter.key} className="space-y-2"><label className="text-sm font-semibold">{filter.label}</label><MultiSelectDropdown label={null} placeholder={`Select ${filter.label.toLowerCase()}...`} options={currentOptions} selectedIds={selected} onSelectionChange={ids => setTempFilters(prev => ({
                  ...prev,
                  [filter.key]: ids
                }))} /></div>;
            }
            return null;
          })}<div className="flex justify-end pt-2"><Button size="sm" onClick={handleApply} className="bg-gray-100 hover:bg-gray-200 text-gray-900 border-0">Apply</Button></div></div></PopoverContent></Popover>;
    $[37] = canFilter;
    $[38] = filters;
    $[39] = handleApply;
    $[40] = isOpen;
    $[41] = tagOptions;
    $[42] = tagsEnabled;
    $[43] = tempFilters;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t21 || $[46] !== t22) {
    t23 = <div className="flex flex-col sm:flex-row gap-4">{t21}{t22}</div>;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== activeFilters?.tags?.length) {
    t24 = activeFilters?.tags?.length > 0 && <span className="text-sm font-bold mr-2">Tags:</span>;
    $[48] = activeFilters?.tags?.length;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== renderActiveFilters) {
    t25 = renderActiveFilters();
    $[50] = renderActiveFilters;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t24 || $[53] !== t25) {
    t26 = <div className="flex flex-wrap gap-2 items-center">{t24}{t25}</div>;
    $[52] = t24;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t20 || $[56] !== t23 || $[57] !== t26) {
    t27 = <div className={t20}>{t23}{t26}</div>;
    $[55] = t20;
    $[56] = t23;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  return t27;
});